import React, { useEffect, useState } from "react";
import { Attachment, Button, Checkbox, Col, DragDrop, HelpText, Input, Row, Slider, Toast, Tooltip, Select, DatePicker} from "@kaleyra/emmy";
import Styles from './index.module.scss';
import { useTranslation } from "react-i18next";
import InfoIcon from '../../../../assets/info-icon-light.svg';
import { isEmpty } from "lodash";
import { validateTxtlyUrl } from "../../../../utils";
import moment, { Moment } from 'moment';

export type MapStateType = {
    viewData: any;
    isEdit: any;
};

export type DispatchPropsType = {
    onSave: (formData: any, callback: (resp: any, flag: boolean) => void) => void;
    onEdit: (formData: any, callback: (resp: any, flag: boolean) => void) => void;
    onClose: () => void;
    onLoad: (filters?: any, SearchStr?: string) => void;
    onFileUpload: (formData: any, callback: (resp: any, flag: boolean) => void) => void;
};

export type OwnProps = {
    onMultipleClose?: (flag: boolean) => void;
}

const sliderTabs = [
    { label: 'Enter Manually', value: 'manual' },
    { label: 'Upload File', value: 'upload' }
];

type sliderTabOptionType = "manual" | "upload";

const URLShortenerForm: React.FC<MapStateType & DispatchPropsType & OwnProps> = ({
    onSave, onClose, isEdit, viewData, onEdit, onLoad, onFileUpload, onMultipleClose }) => {
    const { t } = useTranslation();
    const isEditForm = isEdit.id; 
    const {
        SearchStr,
        filters
    } = isEdit?.extraParam;
    const [originalUrl, setOriginalUrl] = useState<string>("");
    const [urlTitle, setUrlTitle] = useState<string>("");
    const textlyLabel = process.env.REACT_APP_TEXTLY_LABEL || "";
    const [slug, setSlug] = useState<string>("");
    const [callbackUrl, setCallbackUrl] = useState<string>("");
    const [trackUserStatus, setTrackUserStatus] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<sliderTabOptionType>("manual");
    const [uploadedDocument, setUploadedDocument] = useState<any>([]);
    const [isFileAttached, setIsFileAttached] = useState<boolean>(false);
    const [fileName, setFileName] = useState('');
    const [customDomainsOption,setCustomDomainsOption] = useState<{ key: string, value: any}[]>([]);
    const [selectedDomain,setSelectedDomain] = useState<string>(textlyLabel);
    const [selecteddate, setSelectedDate] = useState<Moment | null>(null);

    useEffect(() => {
        if (isEdit.id) {
            const dataById = viewData.find((data: any) => data.id.toString() === isEdit.id.toString());
            const isAttached = dataById?.type.toLowerCase() === "attachment";
            setOriginalUrl(dataById?.url);
            setUrlTitle(dataById?.title);
            setSlug(dataById?.slug);
            setTrackUserStatus((dataById?.is_advanced === 1) ? true : false);
            setCallbackUrl(dataById?.callback_url);
            setSelectedDomain(dataById.domain? dataById.domain: textlyLabel);
            setSelectedDate(dataById?.expiry ? moment(new Date(dataById?.expiry)): null);
            if (isAttached) {
                setIsFileAttached(true);
                setUploadedDocument(dataById?.file_name);
                setActiveTab("upload");
            }
        }
        setCustomDomainsOption([...isEdit?.extraParam?.domain, {key: textlyLabel,value: textlyLabel}]);
    }, []);

    const formatPostData = (): FormData => {
        let formDataOb = new FormData();
        formDataOb.append('url', originalUrl);
        formDataOb.append('url_title', urlTitle);
        if (!isEmpty(textlyLabel)) {
            formDataOb.append('label', selectedDomain);
        }
        if (!isEmpty(slug)) {
            formDataOb.append('slug', slug);
        }
        if (isEdit?.id && isEdit?.extraParam?.appid) {
            formDataOb.append('id', isEdit.id)
            formDataOb.append('appid', isEdit.extraParam.appid)
        }
        if (!isEmpty(callbackUrl)) {
            formDataOb.append('callback_url', callbackUrl);
        }
        if (isFileAttached) {
            formDataOb.append('attachment', "1");
            formDataOb.append('file_name', fileName);
        }
        if(selecteddate){
            formDataOb.append('expiry', `${selecteddate?.format('YYYY-MM-DD')} ${moment().format('HH:mm:ss')}`);
        }
        formDataOb.append('is_advanced', trackUserStatus ? "on" : "off");
        return formDataOb;
    }

    const handleSaveCallback = (response: any, flag: boolean) => {
        if (flag) {
            Toast(Boolean(isEditForm) ? `${t('BLD02002')}` : `${t('BLD02003')}`, "success");
            onLoad(filters, SearchStr);
            onClose();
        } else {
            Toast(response.data?.message, "error")
        }
    }

    const handleFormSave = () => {
        const postRequestBody = formatPostData();
        if (Boolean(isEditForm)) {
            onEdit(postRequestBody, handleSaveCallback);
        } else {
            onSave(postRequestBody, handleSaveCallback)
        }
    }

    const isTitleValid = (): boolean => {
        return urlTitle?.length !== 0 && urlTitle?.length <= 100;
    }

    const isSlugValid = (): boolean => {
        const slugRegex = /^[A-Za-z0-9-]*$/;
        const result = slug.match(slugRegex);
        return (slug?.length === 0 || (slug.length <= 10 && result !== null));
    }

    const isCallbackURLValid = (): boolean => {
        return callbackUrl === undefined || callbackUrl?.length === 0 || validateTxtlyUrl(callbackUrl);
    }

    const isFormValid = (): boolean => {
        return validateTxtlyUrl(originalUrl) && isSlugValid() && isTitleValid() && isCallbackURLValid()
    }

    const renderTooltipBody = () => {
        return (
            <div className={Styles.tooltipBodyWrapper}>
                <span>{t('BLD02004')}</span>
                <span>{t('BLD02005')}</span>
            </div>
        )
    }

    const handleFileUpload = (res: any, flag: boolean) => {
        if (flag) {
            setOriginalUrl(res.data.url);
            Toast(res?.message, "success");
            setIsFileAttached(true);
            setFileName(res.data.title);
        } else {
            Toast(res?.message || t('chaEmptyPricing'), "error");
        }
    }

    const onFileSelect = (file: any, isValid: boolean) => {
        if (isValid) {
            const formData = new FormData();
            formData.append('file', file[0]);
            setUploadedDocument(file);
            onFileUpload(formData, handleFileUpload);
        } else {
            Toast(t('F104'), "error");
        }
    }

    const getFileType = (): string => {
        if (Boolean(isEditForm)) {
            if (typeof (uploadedDocument) === "string") {
                return uploadedDocument;
            }
            return uploadedDocument.length ? uploadedDocument[0]?.name.split(".")[1] : uploadedDocument?.split(".")[1];
        }
        return uploadedDocument[0]?.name.split(".")[1];
    }

    const getFileName = (): string => {
        if (typeof (uploadedDocument) === "string") {
            return uploadedDocument;
        }
        if (uploadedDocument.length) {
            return uploadedDocument[0]?.name || "";
        }
        return uploadedDocument;
    }

    const onDeleteAttachment = () => {
        setUploadedDocument([]);
        setIsFileAttached(false);
        setOriginalUrl('')
        setFileName('');
    }

    const renderFooterBody = () => {
        return <div className={Styles.footerPosition}>
            <Button type="tertiary" onClick={onClose}>
                {t('mainCancel')}
            </Button>
            <Button type="primary" onClick={() => handleFormSave()} disabled={!isFormValid()}>
                {t('mainSave')}
            </Button>
        </div>
    }

    const renderFormBody = () => {
        return (
            <>
                <div>
                    <Input label={t('BLD02007')} placeholder={t('BLD02007')} value={urlTitle} onChange={(e: any) => setUrlTitle(e.target.value)} bottomLabelType={(urlTitle?.length === 0 || isTitleValid() ? "default" : "error")} />
                </div>
                {activeTab === "upload" ?
                    <>
                        {isEmpty(uploadedDocument) || !isFileAttached ?
                            <div className={Styles.dragDropWrapper}>
                                <DragDrop multiple={false} onChange={onFileSelect} />
                            </div> :
                            <>
                                <div className={Styles.attachmentWrapper}>
                                    <Attachment fileType={getFileType()} fileName={getFileName()} id={"id"} onClose={() => onDeleteAttachment()} />
                                </div>
                                {!isEmpty(originalUrl) && <div className={Styles.attachmentTextWrapper}>
                                    <HelpText type="draft" label={`${t('urlShortnerSuccessFileUploadLink')}: ${originalUrl}`} />
                                </div>}
                            </>
                        }
                    </> :
                    <div>
                        <Input label={t('BLD02006')} placeholder={t('BLD02006')} disabled={isFileAttached} value={originalUrl} onChange={(e: any) => setOriginalUrl(e.target.value)} bottomLabelType={(originalUrl?.length === 0 || validateTxtlyUrl(originalUrl)) ? "default" : "error"} />
                    </div>}
                <div>
                    <span className={Styles.formLabel}>{t('BLD02010')}</span>
                    <Row className={Styles.twoColumnFormRow}>
                        <Col gutter size={6}>
                            {/* <Input label={t('BLD02008')} disabled placeholder={t('BLD02008')} value={textlyLabel} /> */}
                            <Select label={t('BLD02008')} placeholder={t('BLD02008')} options={customDomainsOption} value={selectedDomain} onChange={(e)=>setSelectedDomain(e.toString())} disabled={isEdit.id?true:false}/>
                        </Col>
                        <Col size={6}>
                            <Input label={t('BLD02009')} disabled={isEditForm} placeholder={t('BLD02009')}
                                suffix={<Tooltip title={renderTooltipBody()} placement="left"><img src={InfoIcon}></img></Tooltip>}
                                value={slug} onChange={(e: any) => setSlug(e.target.value)}
                                bottomLabelType={slug.length === 0 || isSlugValid() ? "default" : "error"} />
                        </Col>
                    </Row>
                </div>
                <div>
                    <Input label={t('BLD02011')} placeholder={t('BLD02011')} value={callbackUrl} onChange={(e: any) => setCallbackUrl(e.target.value)} bottomLabelType={isCallbackURLValid() ? "default" : "error"} />
                </div>
                <Row>
                    <Col size={12}>
                        <Checkbox label={t('BLD02012')} className={Styles.checkbox} checked={trackUserStatus} onChange={(event: any) => setTrackUserStatus(event.target.checked)} />
                    </Col>
                </Row>
                <div className={Styles.datePickerWrapper}>
					<div>{t('urlExpiryHeading')}</div>
					<DatePicker
						style={{ width: '100%' }}
						value={selecteddate}
                        allowClear={isEdit.id ? false: true}
						onChange={(date: any) => setSelectedDate(date)}
                        showToday={false}
						disabledDate={(date) => {
							return (
								date &&
								!(
									date >
										moment().add(1, 'day').startOf('day') &&
									date < moment().add(1, 'year').endOf('day')
								)
							);
						}}
					/>
					<div className={Styles.desc}>{t('urlExpiryDescption')}</div>
				</div>
            </>
        );
    }

    return (
        <Slider
            containerId="root-modal"
            onClose={onClose}
            visibility={true}
            title={Boolean(isEditForm) ? t('BLD02013') : t('BLD02014')}
            tabs={sliderTabs}
            initActiveTab={activeTab}
            onTabClick={(tab: string) => setActiveTab(tab as sliderTabOptionType)}
            footer = {renderFooterBody()}
        >
            <div className={Styles.container}>
                {renderFormBody()}
            </div>
        </Slider>
    )
}

export default URLShortenerForm;